import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import {
  AnalyticsContextProvider,
  createGoogleAnalyticsProvider,
  createDebugProvider,
  createNoopProvider,
  type AnalyticsProvider,
} from './lib/analytics'
import App from './App'
import './globals.css'

function buildAnalyticsProvider(): AnalyticsProvider {
  // The Software Portfolio GA4 web stream, supplied by the PROD pipeline only
  // (`GA4_MEASUREMENT_ID` in terraform/prod/cicd.tf → VITE_GA4_MEASUREMENT_ID
  // in buildspec-frontend.yml). The dev pipeline sets neither, so a dev build
  // gets an empty id and the noop provider below.
  //
  // THE LITERAL THAT USED TO SIT HERE WAS THE BUG (removed 2026-09-05). It read
  // `VITE_GA4_MEASUREMENT_ID || 'G-VY20J230JE'`, and its comment argued the
  // `import.meta.env.DEV` branch made that safe — "DEV uses the debug provider,
  // so local runs never report to the prod property". True of local runs, and
  // false of the dev DEPLOY: DEV is a build-MODE flag, so it is false for every
  // production-mode build, the dev environment's included. The baked literal
  // then became the id for a site nobody meant to measure.
  //
  // Same defect, same day, as the studio site's `PROD ? literal : undefined`
  // (see the studio main.tsx files and docs/conventions/frontend.md §7) — where
  // it was measured putting dev.fuguelab.com traffic into the production
  // property. Two spellings of one mistake: a build mode is not an environment.
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID
  if (import.meta.env.DEV) {
    return createDebugProvider()
  }
  if (!measurementId) {
    console.warn('[analytics] VITE_GA4_MEASUREMENT_ID not set; analytics disabled.')
    return createNoopProvider()
  }
  return createGoogleAnalyticsProvider({ measurementId })
}

// eslint-disable-next-line react-refresh/only-export-components -- app entry file (mounts the tree below); not a Fast Refresh target, no exports by design
function Root() {
  const [analytics] = useState<AnalyticsProvider>(() => buildAnalyticsProvider())
  return (
    <AnalyticsContextProvider provider={analytics}>
      <HelmetProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HelmetProvider>
    </AnalyticsContextProvider>
  )
}

const rootEl = document.getElementById('root')!
const tree = (
  <StrictMode>
    <Root />
  </StrictMode>
)

// Prerendered pages (see client/prerender.mjs) ship fully-rendered markup inside
// #root so ATS parsers and non-JS LLM crawlers read real content. The site itself
// relies on heavy GSAP/Lenis animation hooks that mutate the DOM at runtime
// (splitting hero text into per-char spans, applying scroll-reveal transforms),
// so the captured markup intentionally differs from React's clean initial render.
// True hydrateRoot over that markup throws recoverable hydration mismatches, so we
// deliberately render fresh on the client: clear the prerendered markup and mount
// with createRoot. Crawlers still get the static content; the live SPA is identical
// to before this change (clean mount, animations run normally).
//
// If the animation hooks are ever made prerender-aware (no-op under a capture
// flag), switch this back to hydrateRoot for a zero-rerender first paint.
if (rootEl.hasChildNodes()) {
  rootEl.innerHTML = ''
}
createRoot(rootEl).render(tree)
